/**
 * Reducer — the only function that mutates GameState.
 *
 * Pattern: state + action → newState. No side effects. Pure functions only.
 *
 * # Current state (post combat-genre-pivot)
 *
 * The legacy turn-based card combat reducer was removed when SAVE_VERSION
 * bumped from 1 to 2. The new real-time isometric combat reducer is being
 * built in `combat/combatReducer.ts` and isn't wired up yet.
 *
 * Until the new combat reducer ships:
 *   - All actions reaching `reduce()` are passed through as no-op.
 *   - Save lifecycle and telemetry actions are handled by their respective
 *     reducers (Phase B / future Phase C-telemetry), composed at the
 *     gameStore level in packages/ui.
 *
 * # Why a stub instead of deletion
 *
 * `reduce` is a public export consumed by `gameStore` and tests. Removing
 * it would break the import graph in many places. Stubbing keeps the
 * function signature stable and the import surface unchanged. When the
 * v2 combat reducer lands, it slots into this same dispatch path.
 *
 * # The exhaustiveness story
 *
 * Currently `CombatAction = never` (empty union, see `actions/combat.ts`).
 * This means `switch (action.type)` has no cases to handle and TypeScript's
 * `never`-narrowing in the default branch is automatically satisfied. When
 * the v2 CombatAction union grows variants, this file gains case branches
 * for them, and TypeScript will force exhaustive handling.
 *
 * @see {@link ./actions/index.ts} — Action union composition
 * @see {@link ./actions/combat.ts} — CombatAction union (currently empty)
 * @see {@link ./combat/} — fixedPoint + trigTable math foundation for the
 *      future combat reducer
 */

import type { GameState } from './state';
import type { Action } from './actions';

// Re-export Action for backward compatibility with existing consumers
// (packages/ui imports Action from @aevons-wake/core via reducer.ts in the
// pre-Phase-A barrel; this preserves that import path).
export type { Action } from './actions';

/**
 * Apply an action to the game state, producing a new state.
 *
 * Pure function. Same input → same output. No side effects, no I/O,
 * no time-based randomness. Replay-safe.
 *
 * Currently a stub: every action is passed through as no-op until the
 * v2 combat reducer is implemented. The structure is preserved so the
 * v2 reducer can slot into this same dispatch.
 */
export function reduce(state: GameState, action: Action): GameState {
  // Future: switch on action.type once CombatAction has variants.
  // For now, the empty union means TypeScript correctly types `action`
  // as the union of SaveAction | TelemetryAction (or just the active
  // domains), all of which are handled by other reducers composed at
  // the gameStore level.
  //
  // Suppress unused-parameter warning while keeping the parameter for the
  // public function signature.
  void action;
  return state;
}
